import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  IconButton,
  Button,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Paper,
  Avatar,
  useTheme,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Restore as RestoreIcon,
  Add as AddIcon,
  Assignment as BorrowIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  Inventory as InventoryIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  AttachMoney as MoneyIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import ListFilterBar from '../../common/ListFilterBar';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import StatCards from '../../common/StatCards';
import TableList from '../../common/TableList';
import ListBulkToolbar from '../../common/ListBulkToolbar';
import { BORROW_BULK_CONFIG } from '../../../utils/bulkUpdateConfigs';
import { useListBulkSelection, bulkTableProps, createBulkHandlers } from '../../../utils/listBulkSelection';

import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { canManageInventory } from '../../../utils/inventoryPermissions';
import { SUCCESS, TEXT_PRIMARY, TEXT_SECONDARY } from '../../common/SaaSTokens';
import dayjs from 'dayjs';

const BorrowList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { user: currentUser } = useAuth();
const { selectedIds, setSelectedIds, clearSelection } = useListBulkSelection();
  
  
  const [borrows, setBorrows] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const [activeStatFilter, setActiveStatFilter] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBorrow, setSelectedBorrow] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const canManageBorrow = canManageInventory(currentUser);
  const canCreateBorrow = canManageBorrow;
  const canEditBorrow = canManageBorrow;
  const canDeleteBorrow = canManageBorrow;

  const filterOptions = [
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'pending', label: 'Pending' },
        { value: 'approved', label: 'Approved' },
        { value: 'returned', label: 'Returned' },
        { value: 'overdue', label: 'Overdue' },
        { value: 'archived', label: 'Archived' },
      ],
    },
    {
      name: 'penalty_due',
      label: 'Penalty due',
      type: 'select',
      options: [
        { value: 'today', label: 'Penalty date is today' },
        { value: 'next_7_days', label: 'Penalty date within 7 days' },
        { value: 'past', label: 'Past penalty date (not returned)' },
      ],
    },
    {
      name: 'stock_id',
      label: 'Inventory item',
      type: 'select',
      searchable: true,
      options: stocks.map((s) => ({ value: String(s.id), label: s.things })),
    },
  ];

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    fetchBorrows({ forceRefresh: Boolean(location.state?.refreshList) });
    fetchStocks();
  }, [currentUser, location.pathname, location.state?.refreshList]);

  useEffect(() => {
    const stockId = location.state?.filterStockId;
    if (!stockId) return;
    setFilters({ stock_id: String(stockId) });
    setShowFilters(true);
    setPage(0);
    if (location.state?.stockLabel) {
      setSuccess(`Showing borrow records for “${location.state.stockLabel}”`);
    }
  }, [location.state?.filterStockId, location.state?.stockLabel]);

  const fetchBorrows = async ({ forceRefresh = false, archived = false } = {}) => {
    try {
      setLoading(true);
      const response = await api.get('/borrows', {
        ...(forceRefresh ? { cache: false } : {}),
        ...(archived ? { params: { archived: 1 } } : {}),
      });
      setBorrows(Array.isArray(response.data) ? response.data.filter(Boolean) : []);
      setError(null);
    } catch (error) {
      console.error('Error fetching borrows:', error);
      setError('Failed to load borrow records. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchStocks = async () => {
    try {
      const [activeResponse, archivedResponse] = await Promise.all([
        api.get('/stocks'),
        api.get('/stocks', { params: { archived: 1 } }),
      ]);
      setStocks([
        ...(Array.isArray(activeResponse.data) ? activeResponse.data : []),
        ...(Array.isArray(archivedResponse.data) ? archivedResponse.data : []),
      ]);
    } catch (error) {
      console.error('Error fetching stocks:', error);
    }
  };

  const handleDelete = (borrow) => {
    setSelectedBorrow(borrow);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedBorrow?.id) return;

    const borrowId = selectedBorrow.id;
    const borrowerName = selectedBorrow.borrower_name || 'Borrow record';

    try {
      setDeleteLoading(true);
      await api.post(`/borrows/${borrowId}/archive`);
      await fetchBorrows({ forceRefresh: true });
      setFilters((current) => {
        const next = { ...current };
        delete next.status;
        return next;
      });
      setActiveStatFilter('all');
      setPage(0);
      setDeleteDialogOpen(false);
      setSelectedBorrow(null);
      setSuccess(`Borrow record for "${borrowerName}" has been archived successfully!`);
    } catch (error) {
      console.error('Error deleting borrow:', error);
      setError(error.response?.data?.message || 'Failed to archive borrow record. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRestore = async (borrow) => {
    try {
      await api.post(`/borrows/${borrow.id}/restore`);
      setBorrows((items) => items.filter((item) => item.id !== borrow.id));
      setSuccess(`Borrow record for "${borrow.borrower_name || 'Borrower'}" has been restored successfully!`);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to restore borrow record. Please try again.');
    }
  };

  const handleBulkArchive = async () => {
    const ids = [...(selectedIds || [])];
    if (!ids.length) {
      setError('No selected borrow records to archive.');
      return;
    }

    try {
      await Promise.all(ids.map((borrowId) => api.post(`/borrows/${Number(borrowId)}/archive`)));
      const idSet = new Set(ids.map(String));
      setBorrows((items) => items.filter((item) => !idSet.has(String(item.id))));
      clearSelection();
      setError(null);
      setSuccess(`${ids.length} borrow record(s) archived successfully.`);
      await fetchBorrows({ forceRefresh: true });
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to archive selected borrow records. Please try again.');
      throw error;
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (newFilters) => {
    const archivedViewChanged = (filters.status === 'archived') !== (newFilters.status === 'archived');
    const nextActive = newFilters.status ? newFilters.status : 'all';
    setActiveStatFilter(nextActive);
    setFilters(newFilters);
    setPage(0);
    if (archivedViewChanged) {
      clearSelection();
      fetchBorrows({ forceRefresh: true, archived: newFilters.status === 'archived' });
    }
  };

  const handleClearFilters = () => {
    const wasArchived = filters.status === 'archived';
    setFilters({});
    setActiveStatFilter('all');
    setSearchTerm('');
    setPage(0);
    if (wasArchived) fetchBorrows({ forceRefresh: true });
  };

  const handleStatCardClick = (_, index) => {
    const filterMap = ['all', 'pending', 'approved', 'overdue'];
    const selectedFilter = filterMap[index] || 'all';
    const nextFilter = activeStatFilter === selectedFilter ? 'all' : selectedFilter;
    setActiveStatFilter(nextFilter);

    const nextFilters = { ...filters };
    if (nextFilter === 'all') {
      delete nextFilters.status;
    } else {
      nextFilters.status = nextFilter;
    }
    setFilters(nextFilters);
    setPage(0);
  };

  const activeStatIndex =
    activeStatFilter === 'pending' ? 1 :
    activeStatFilter === 'approved' ? 2 :
    activeStatFilter === 'overdue' ? 3 :
    activeStatFilter === 'archived' ? -1 :
    0;

  const getStockName = (stockId) => {
    const stock = stocks.find(s => s.id === stockId);
    return stock ? stock.things : 'Unknown Item';
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 'warning';
      case 'approved': return 'info';
      case 'returned': return 'success';
      case 'overdue': return 'error';
      case 'archived': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return <PendingIcon />;
      case 'approved': return <CheckCircleIcon />;
      case 'returned': return <CheckCircleIcon />;
      case 'overdue': return <WarningIcon />;
      case 'archived': return <ArchiveIcon />;
      default: return <PendingIcon />;
    }
  };

  const cellTextSx = { fontWeight: 500, color: alpha(TEXT_PRIMARY, 0.9) };
  const cellMutedSx = { fontWeight: 500, color: alpha(TEXT_SECONDARY, 0.95) };

  const isOverdue = (borrow) => {
    if (borrow.archived_at || borrow.status === 'returned') return false;
    if (!borrow.penalty_date) return false;
    return dayjs(borrow.penalty_date).isBefore(dayjs(), 'day');
  };

  const getDisplayStatus = (borrow) => (borrow.archived_at ? 'archived' : (isOverdue(borrow) ? 'overdue' : borrow.status));

  const isReturnedBorrow = (borrow) => Boolean(borrow.archived_at) || borrow.status?.toLowerCase() === 'returned';
  const isUnreturnedActiveBorrow = (borrow) => Boolean(borrow) && !borrow.archived_at && borrow.status?.toLowerCase() !== 'returned';

  const compareBorrowsByTime = (a, b) => {
    const aReturned = isReturnedBorrow(a);
    const bReturned = isReturnedBorrow(b);
    if (aReturned !== bReturned) return aReturned ? 1 : -1;

    const penaltyDiff = dayjs(b.penalty_date).valueOf() - dayjs(a.penalty_date).valueOf();
    if (penaltyDiff !== 0) return penaltyDiff;
    return dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf();
  };

  const filteredBorrows = borrows
    .filter((borrow) => {
      const q = searchTerm.toLowerCase();
      const matchesSearch =
        !searchTerm
        || borrow.borrower_name?.toLowerCase().includes(q)
        || borrow.borrower_contact?.toLowerCase().includes(q)
        || getStockName(borrow.stock_id).toLowerCase().includes(q);

      const actualStatus = getDisplayStatus(borrow);

      const matchesFilters = Object.entries(filters).every(([key, value]) => {
        if (!value || value === 'all') return true;
        if (key === 'status') return actualStatus === value;
        if (key === 'stock_id') return String(borrow.stock_id) === String(value);
        if (key === 'penalty_due' && borrow.penalty_date) {
          const due = dayjs(borrow.penalty_date).startOf('day');
          const today = dayjs().startOf('day');
          if (value === 'today') return due.isSame(today, 'day');
          if (value === 'next_7_days') {
            return !due.isBefore(today, 'day') && !due.isAfter(today.add(7, 'day'), 'day');
          }
          if (value === 'past') return due.isBefore(today, 'day') && borrow.status !== 'returned';
        }
        return true;
      });

      return matchesSearch && matchesFilters;
    })
    .sort(compareBorrowsByTime);

  // ─── STATS CALCULATED FROM ALL DATA (NOT FILTERED) ───────────────────────────────
  const totalBorrows = borrows.length;
  const pendingBorrows = borrows.filter(borrow => !borrow.archived_at && borrow.status === 'pending').length;
  const overdueBorrows = borrows.filter((borrow) => isOverdue(borrow)).length;
  const activeBorrows = borrows.filter(borrow => !borrow.archived_at && borrow.status === 'approved').length;

  const statsData = [
    { icon: BorrowIcon, value: totalBorrows, label: 'Total Borrows', color: 'info' },
    { icon: PendingIcon, value: pendingBorrows, label: 'Pending Approval', color: 'warning' },
    { icon: CheckCircleIcon, value: activeBorrows, label: 'Approved Borrows', color: 'success' },
    { icon: WarningIcon, value: overdueBorrows, label: 'Overdue Items', color: 'error' },
  ];
  const activeStocks = stocks.filter((stock) => stock && !stock.archived_at);
  const hasAvailableStock = activeStocks.some((stock) => Number(stock.available_quantity) > 0);
  const addBorrowDisabledMessage = stocks.length === 0
    ? 'Create at least one stock item before creating a borrow record.'
    : activeStocks.length === 0
      ? 'All stock items are archived. Set at least one stock item to Active before creating a borrow record.'
      : 'No active stock currently has available quantity.';

  const borrowColumns = [
    { 
      key: 'borrower_name', 
      label: 'Borrower', 
      icon: PersonIcon,
      minWidth: 160,
      render: (borrow) => (
        <Typography variant="body2" sx={cellTextSx}>
          {borrow.borrower_name}
        </Typography>
      )
    },
    { 
      key: 'borrower_contact', 
      label: 'Contact', 
      icon: PhoneIcon,
      minWidth: 140,
      render: (borrow) => (
        <Typography variant="body2" sx={cellMutedSx}>
          {borrow.borrower_contact}
        </Typography>
      )
    },
    {
      key: 'stock_id',
      label: 'Item',
      icon: InventoryIcon,
      minWidth: 180,
      sortAccessor: (borrow) => getStockName(borrow.stock_id),
      render: (borrow) => (
        <Typography
          variant="body2"
          component="span"
          role="link"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            if (borrow.stock_id) navigate(`/stocks/${borrow.stock_id}`);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              if (borrow.stock_id) navigate(`/stocks/${borrow.stock_id}`);
            }
          }}
          sx={{
            ...cellTextSx,
            color: theme.palette.info.light,
            cursor: borrow.stock_id ? 'pointer' : 'default',
            textDecoration: 'none',
            '&:hover': borrow.stock_id ? { textDecoration: 'underline' } : {},
          }}
        >
          {getStockName(borrow.stock_id)}
        </Typography>
      ),
    },
    { 
      key: 'quantity', 
      label: 'Quantity',
      minWidth: 100,
      render: (borrow) => (
        <Typography variant="body2" sx={cellTextSx}>
          {borrow.quantity}
        </Typography>
      )
    },
    {
      key: 'penalty_date',
      label: 'Penalty / Return',
      icon: ScheduleIcon,
      minWidth: 140,
      sortAccessor: (borrow) => dayjs(borrow.penalty_date).valueOf(),
      render: (borrow) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35 }}>
          <Typography variant="body2" sx={cellTextSx}>
            {borrow.penalty_date ? dayjs(borrow.penalty_date).format('MMM D, YYYY') : '—'}
          </Typography>
          <Typography variant="caption" sx={{ ...cellMutedSx, lineHeight: 1.3 }}>
            Return:{' '}
            {borrow.return_date ? dayjs(borrow.return_date).format('MMM D, YYYY') : '—'}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      minWidth: 140,
      sortAccessor: (borrow) => getDisplayStatus(borrow),
      render: (borrow) => {
        const actualStatus = getDisplayStatus(borrow);
        return (
          <Chip
            icon={getStatusIcon(actualStatus)}
            label={actualStatus.charAt(0).toUpperCase() + actualStatus.slice(1)}
            color={getStatusColor(actualStatus)}
            size="small"
            sx={actualStatus === 'archived' ? {
              color: theme.palette.mode === 'dark' ? '#E2E8F0' : '#475569',
              backgroundColor: alpha('#94A3B8', theme.palette.mode === 'dark' ? 0.2 : 0.14),
              border: `1px solid ${alpha('#94A3B8', 0.45)}`,
              fontWeight: 700,
              '& .MuiChip-icon': {
                color: theme.palette.mode === 'dark' ? '#CBD5E1' : '#64748B',
              },
            } : undefined}
          />
        );
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      sortable: false,
      minWidth: 160,
      render: (borrow) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Tooltip title="View Borrow" arrow>
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/borrows/${borrow.id}`);
              }}
              sx={{
                color: theme.palette.info.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                },
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canEditBorrow && !borrow.archived_at && (
            <Tooltip title="Edit Borrow" arrow>
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/borrows/edit/${borrow.id}`);
                }}
                sx={{
                  color: theme.palette.warning.main,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.warning.main, 0.1),
                  },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {borrow.archived_at && (
            <Tooltip title="Restore Borrow" arrow>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); handleRestore(borrow); }}
                sx={{ color: theme.palette.success.main }}
              >
                <RestoreIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canDeleteBorrow && !borrow.archived_at && (
            <Tooltip title="Archive Borrow" arrow>
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(borrow);
                }}
                sx={{
                  color: theme.palette.error.main,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                  },
                }}
              >
                <ArchiveIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  if (!currentUser) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <FeedbackMessage
          message="Please log in to view this page."
          severity="error"
          sx={{ borderRadius: 2 }}
        />
      </Container>
    );
  }

  if (!canManageBorrow) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <FeedbackMessage
          message="You don't have permission to view this page. Only Team Leaders and Inventory bureau members can access borrow management."
          severity="error"
          sx={{ borderRadius: 2 }}
        />
      </Container>
    );
  }

  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
      <Loading message="Loading borrow list" size={56} variant="primary" />
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Header
        title="Borrow Management"
        subtitle="Track and manage equipment borrowing and returns"
        icon={BorrowIcon}
        actionButton={canCreateBorrow ? (
          <Tooltip
            title={hasAvailableStock ? '' : addBorrowDisabledMessage}
            arrow
          >
            <span>
              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={() => navigate('/borrows/add')}
                disabled={!hasAvailableStock}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.3)',
                  },
                  borderRadius: 2,
                  px: 3,
                  py: 1.5
                }}
              >
                Add Borrow
              </Button>
            </span>
          </Tooltip>
        ) : null}
      />

      {/* Error Message */}
      <FeedbackMessage
        message={error}
        severity="error"
        onClose={() => setError(null)}
        sx={{ mb: 3, fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5', boxShadow: '0 4px 20px rgba(239,68,68,0.12)' }}
      />

      {/* Success Message */}
      <FeedbackMessage
        message={success}
        severity="success"
        onClose={() => setSuccess(null)}
        sx={{ mb: 3, fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', fontWeight: 500, background: alpha(SUCCESS, 0.12), border: `1px solid ${alpha(SUCCESS, 0.25)}`, color: alpha(SUCCESS, 0.95) }}
      />

      <StatCards stats={statsData} onCardClick={handleStatCardClick} activeIndex={activeStatIndex} />

      <ListFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        filterOptions={filterOptions}
        searchPlaceholder="Search by borrower name, contact, or item..."
      />
      {filters.status !== 'archived' && <ListBulkToolbar
        config={BORROW_BULK_CONFIG}
        currentUser={currentUser}
        selectedIds={selectedIds}
        onClearSelection={clearSelection}
        {...createBulkHandlers({
          endpoint: BORROW_BULK_CONFIG.endpoint,
          setItems: setBorrows,
          selectedIds,
          clearSelection,
          setSuccess,
          successLabel: 'borrow record(s)',
          deleteActionPastTense: 'archived',
          refetch: async (options) => {
            await fetchBorrows(options);
            setFilters((current) => {
              const next = { ...current };
              delete next.status;
              return next;
            });
            setActiveStatFilter('all');
            setPage(0);
          },
          setError,
          filterDeleteIds: (ids) => {
            const activeIds = ids.filter((id) => {
              const borrow = borrows.find((item) => String(item.id) === String(id));
              return borrow && !borrow.archived_at;
            });
            if (activeIds.length === 0) {
              setError('The selected borrow records are already archived.');
              return false;
            }
            return activeIds;
          },
        })}
        onDeleteSuccess={handleBulkArchive}
      />}


      <TableList
        columns={borrowColumns}
        data={filteredBorrows}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        {...(filters.status !== 'archived'
          ? bulkTableProps(BORROW_BULK_CONFIG, currentUser, selectedIds, setSelectedIds)
          : {})}
        loading={loading}
        emptyState={{
          icon: <BorrowIcon />,
          title: 'No borrow records found',
          description: searchTerm || Object.keys(filters).length > 0
            ? 'Try adjusting your search or filter criteria.'
            : 'Get started by creating your first borrow request.',
        }}
        onRowClick={(row) => navigate(`/borrows/${row.id}`)}
      />

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedBorrow(null);
        }}
        onConfirm={confirmDelete}
        loading={deleteLoading}
        itemType="borrow record"
        itemName={selectedBorrow?.borrower_name}
        title="Archive Borrow"
        description={
          isUnreturnedActiveBorrow(selectedBorrow)
            ? 'This borrow record has not been returned yet. Archiving it will remove its quantity from active inventory calculations, so the stock becomes available for new borrow records. Are you sure you want to archive it?'
            : 'The borrow record will be archived and remain in the system for history and audit purposes.'
        }
        confirmLabel="Archive Borrow"
        actionVerb="archive"
      />
    </Container>
  );
};

export default BorrowList;
